import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Image } from "@/src/lib/components/custom/Image";
import Icon from "../../lib/components/custom/Icon";
import { Button } from "../../lib/components/ui/button";
import { Card } from "../../lib/components/ui/card";
import { Input } from "../../lib/components/ui/input";
import { Label } from "../../lib/components/ui/label";
import { Separator } from "../../lib/components/ui/separator";
import { useApi } from "../../lib/hooks/api/use-api";
import Layout from "../layout";

export default function Signup() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { auth } = useApi();
	const { data: session } = auth.useSession();

	// Redirect authenticated users to profile page
	useEffect(() => {
		if (session?.user) {
			navigate({ to: "/profile" });
		}
	}, [session, navigate]);
	const [isLoading, setIsLoading] = useState(false);
	const [formData, setFormData] = useState({
		email: "",
		password: "",
	});
	const [error, setError] = useState("");

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
		if (error) setError(""); // Clear error on input change
	};

	const handleSignup = async (e: React.FormEvent) => {
		e.preventDefault();
		setError("");
		setIsLoading(true);

		// Basic validation
		if (!formData.email.trim()) {
			setError(t("auth.signin.errors.email_required"));
			setIsLoading(false);
			return;
		}

		if (formData.password.length < 6) {
			setError(t("auth.signin.errors.password_min"));
			setIsLoading(false);
			return;
		}

		try {
			const response = await auth.signUp.email({
				email: formData.email,
				password: formData.password,
				name: formData.email.split("@")[0], // Use email prefix as name
			});

			console.log(response);

			if (response.error) {
				setError(
					response.error.message || t("auth.signup.errors.signup_failed"),
				);
			} else {
				console.log("Signup successful:", response.data);
				navigate({ to: "/auth/verify-email" });
			}
		} catch (err) {
			setError(t("auth.signin.errors.unexpected"));
			console.error("Signup error:", err);
		} finally {
			setIsLoading(false);
		}
	};

	const handleGoogleSignup = async () => {
		try {
			await auth.signIn.social({
				provider: "google",
				callbackURL: "/", // Redirect to home after signup
			});
		} catch (err) {
			setError(t("auth.signup.errors.google_failed"));
			console.error("Google signup error:", err);
		}
	};

	return (
		<Layout>
			<div className="p-8 border min-h-full rounded-large bg-gradient-main">
				<div className="max-w-md mx-auto">
					{/* Header */}
					<div className="text-center mb-8">
						<h1 className="text-3xl font-manrope font-semibold mb-2">
							{t("auth.signup.header")}
						</h1>
						<p className="text-muted-foreground">
							{t("auth.signup.subheader")}
						</p>
					</div>

					{/* Signup Form */}
					<Card className="p-6">
						<form onSubmit={handleSignup} className="space-y-4">
							{/* Email Field */}
							<div>
								<Label htmlFor="email">{t("auth.signup.email_label")}</Label>
								<Input
									id="email"
									name="email"
									type="email"
									placeholder={t("auth.signup.email_placeholder")}
									value={formData.email}
									onChange={handleInputChange}
									required
								/>
							</div>

							{/* Password Field */}
							<div>
								<Label htmlFor="password">
									{t("auth.signup.password_label")}
								</Label>
								<Input
									id="password"
									name="password"
									type="password"
									placeholder={t("auth.signup.password_placeholder")}
									value={formData.password}
									onChange={handleInputChange}
									required
								/>
							</div>

							{/* Error Message */}
							{error && (
								<div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md p-3">
									{error}
								</div>
							)}

							{/* Signup Button */}
							<Button
								variant="primary"
								type="submit"
								className="w-full"
								disabled={isLoading}
							>
								{isLoading ? (
									<>
										<Icon name="Loader" className="w-4 h-4 mr-2 animate-spin" />
										{t("auth.signup.creating_account")}
									</>
								) : (
									<div>{t("auth.signup.submit")}</div>
								)}
							</Button>
						</form>

						{/* Divider */}
						<div className="relative my-6">
							<Separator />
							<div className="absolute inset-0 flex items-center justify-center">
								<span className="bg-card px-2 text-xs text-muted-foreground">
									{t("auth.signin.or")}
								</span>
							</div>
						</div>

						{/* Google Signup */}
						<Button
							variant="default"
							className="w-full"
							onClick={handleGoogleSignup}
							disabled={isLoading}
						>
							<Image
								src="/static/icons/google.webp"
								alt="Google"
								width={20}
								height={20}
							/>
							{t("auth.signin.google")}
						</Button>
					</Card>

					{/* Signin Link */}
					<div className="text-center mt-6">
						<p className="text-muted-foreground">
							{t("auth.signup.already_account")}{" "}
							<Link
								to="/auth/signin"
								className="text-primary font-medium hover:underline"
							>
								{t("auth.signup.signin_link")}
							</Link>
						</p>
					</div>

					{/* Legal Links */}
					<div className="text-center mt-6">
						<p className="text-xs text-muted-foreground">
							{t("auth.signup.terms_notice")}{" "}
							<Link
								to="/legal/terms"
								className="text-primary hover:underline font-medium"
							>
								{t("auth.signup.terms_conditions")}
							</Link>{" "}
							{t("auth.signup.and")}{" "}
							<Link
								to="/legal/privacy"
								className="text-primary hover:underline font-medium"
							>
								{t("auth.signup.privacy_policy")}
							</Link>
							.
						</p>
					</div>

					{/* Back to Home */}
					<div className="text-center mt-4">
						<Button variant="ghost" asChild>
							<Link to="/">
								<Icon name="ArrowLeft" className="w-4 h-4 mr-2" />
								{t("auth.signin.back_to_home")}
							</Link>
						</Button>
					</div>
				</div>
			</div>
		</Layout>
	);
}
